import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { muvekkilAPI, MUVEKKIL_BILGI_KEY } from '../api/api';
import { DAVA_TURU_LABELS, NEW_CASE_FILE_STATUS_LABELS } from '../utils/constants';
import PdfGoruntuleyici from '../components/Common/PdfGoruntuleyici';
import { belgeMetniCikar } from '../utils/belgeOnizleme';
import { formatDurusmaTarihi } from '../utils/dateUtils';
import { isNativeApp } from '../utils/nativePicker';
import { whatsappAc } from '../utils/whatsapp';
import { indirmeMumkunMu, downloadBlob } from '../utils/fileDownload';
import { kameraVarMi, evrakFotografiCek } from '../utils/kamera';
import { muvekkilBildirimBaslat } from '../utils/muvekkilBildirim';
import './MuvekkilPortal.css';

/* Bilgilendirme onayi oturum boyunca hatirlanir; kullanici cikip tekrar
   girdiginde yeniden gosterilir. sessionStorage kullanildigi icin sekme
   kapaninca da sifirlanir. */
const ONAY_ANAHTARI = 'muvekkilBilgilendirmeOnay';

/* Cihazda gorüntülenebilecek en büyük belge. Sunucu dosyayi tamamen bellege
   aldigi ve telefon bu boyutu indiremedigi icin, bunun ustundeki belgelerde
   önizleme hic denenmez; kullaniciya dogrudan anlasilir bir aciklama verilir.
   (Gercek ornek: 1,65 GB'lik bir PDF sonsuza kadar "Belge aciliyor..." diyordu.) */
const ONIZLEME_BOYUT_SINIRI = 100 * 1024 * 1024;   // 100 MB

const tarih = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  return t.toLocaleDateString('tr-TR');
};

/** Duruşmaya kalan gün — geçmişse null. */
const kalanGun = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);
  t.setHours(0, 0, 0, 0);
  const fark = Math.round((t - bugun) / 86400000);
  return fark >= 0 ? fark : null;
};

/** Tutarı Türk Lirası biçiminde yazar. */
const para = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
};

const boyut = (b) => {
  const n = Number(b) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

const durumEtiketi = (s) => NEW_CASE_FILE_STATUS_LABELS?.[s] || 'AKTİF';
const turEtiketi = (t, serbest) => {
  // 56 = DİĞER: tür adı dosyada elle yazılmıştır.
  if (Number(t) === 56 && serbest && String(serbest).trim()) return String(serbest).trim();
  return DAVA_TURU_LABELS?.[t] || 'Dosya';
};

/** Değeri olan alanları listeler; boşlar hiç gösterilmez. */
const Alanlar = ({ baslik, satirlar }) => {
  const dolu = satirlar.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (dolu.length === 0) return null;
  return (
    <section className="mp-blok">
      <h4>{baslik}</h4>
      <dl>
        {dolu.map(([k, v]) => (
          <div key={k} className="mp-satir">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const MuvekkilPortal = ({ api = muvekkilAPI, onizlemeModu = false, bilgiDisaridan = null }) => {
  const navigate = useNavigate();
  const onizlemeAcRef = useRef(null);
  const dosyaGirdi = useRef(null);

  const [bilgi] = useState(() => {
    // Onizlemede (personel "muvekkil gozunden" bakarken) muvekkilin adi
    // disaridan gelir; tarayici hafizasindaki personel kaydi kullanilmaz.
    if (bilgiDisaridan) return bilgiDisaridan;
    try { return JSON.parse(localStorage.getItem(MUVEKKIL_BILGI_KEY) || '{}'); }
    catch { return {}; }
  });

  const [dosyalar, setDosyalar] = useState([]);
  /** Büronun WhatsApp numarası; tanımlı değilse düğme gösterilmez. */
  const [buroWhatsapp, setBuroWhatsapp] = useState(null);
  const [secili, setSecili] = useState(null);
  const [belgeler, setBelgeler] = useState([]);
  const [odemeBilgisi, setOdemeBilgisi] = useState(null);
  const [onizleme, setOnizleme] = useState(null);       // {belge, url, tur}
  const [onizlemeYukleniyor, setOnizlemeYukleniyor] = useState(false);
  const [onizlemeYuzde, setOnizlemeYuzde] = useState(null);   // null = boyut bilinmiyor
  const [onizlemeInen, setOnizlemeInen] = useState(0);        // bayt
  const [yuklemeYuzde, setYuklemeYuzde] = useState(null);
  const [odemeYukleniyor, setOdemeYukleniyor] = useState(false);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [belgeYukleniyor, setBelgeYukleniyor] = useState(false);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState('');
  const [durum, setDurum] = useState('');

  /* BILDIRIMLER
     Telefona giden bildirim gecici: muvekkil o an bakmazsa kayboluyor.
     Burasi kalici listeyi gosteriyor. Zildeki sayi ayni zamanda
     uygulama simgesindeki sayiyla ayni kaynaktan geliyor. */
  const [bildirimler, setBildirimler] = useState([]);
  const [okunmamis, setOkunmamis] = useState(0);
  const [bildirimAcik, setBildirimAcik] = useState(false);
  const [bildirimYukleniyor, setBildirimYukleniyor] = useState(false);

  const cikis = useCallback(() => {
    // Onizlemede (personel "muvekkil gozunden" bakarken) cikis calismaz:
    // yoksa avukat kendi oturumundan atilir.
    if (onizlemeModu) return;
    api.cikis();
    navigate('/login');
  }, [navigate, onizlemeModu, api]);

  useEffect(() => {
    let iptal = false;
    (async () => {
      try {
        const liste = await api.dosyalarim();
        if (iptal) return;
        setDosyalar(liste);

        // Oturum gecerli: Face ID aciksa bu cihazin anahtarini tazele.
        if (!onizlemeModu) {
          import('../utils/faceId')
            .then((m) => m.faceIdAnahtariniTazele('muvekkil'))
            .catch(() => {});
        }

        /* Bildirimden gelindiyse (/muvekkil?dosya=123) o dosya acilir;
           yoksa ilk dosya secilir. Birden fazla dosyasi olan muvekkil,
           evragin hangi dosyaya eklendigini aramak zorunda kalmasin. */
        let acilacak = liste.length > 0 ? liste[0] : null;
        try {
          const istenen = new URLSearchParams(window.location.search).get('dosya');
          if (istenen) {
            const bulunan = liste.find((d) => String(d.id ?? d.Id) === String(istenen));
            if (bulunan) acilacak = bulunan;
          }
        } catch (e) { /* yoksay */ }
        if (acilacak) setSecili(acilacak);

        // WhatsApp numarası ayrı bir uçtan gelir; gelmemesi dosyaların
        // görünmesini engellememeli, bu yüzden hatası yutuluyor.
        try {
          const ile = await api.iletisim();
          if (!iptal && (ile?.hazir ?? ile?.Hazir)) {
            setBuroWhatsapp(ile.whatsapp ?? ile.Whatsapp ?? null);
          }
        } catch { /* WhatsApp tanımlı değil — düğme gizli kalır */ }
      } catch (err) {
        if (err?.response?.status === 401) { cikis(); return; }
        setHata('Dosyalarınız yüklenemedi. Lütfen tekrar giriş yapın.');
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    })();
    return () => { iptal = true; };
  }, [cikis]);

  const bildirimleriGetir = useCallback(async () => {
    setBildirimYukleniyor(true);
    try {
      const d = await api.bildirimler();
      setBildirimler(d?.bildirimler ?? []);
      setOkunmamis(d?.okunmamis ?? 0);
    } catch (_) {
      // Bildirim alinamazsa portalin geri kalani etkilenmemeli.
    } finally {
      setBildirimYukleniyor(false);
    }
  }, [api]);

  useEffect(() => { bildirimleriGetir(); }, [bildirimleriGetir]);

  const bildirimleriAc = async () => {
    setBildirimAcik(true);
    await bildirimleriGetir();
    // Acildigi anda hepsi okundu sayiliyor; uygulama simgesindeki sayi
    // da bu kayitlardan hesaplandigi icin boylece sifirlaniyor.
    //
    // LISTE BURADA "OKUNDU" YAPILMIYOR: onceden pencere acilir acilmaz her
    // satir okundu isaretleniyor, okunmamis zemini aninda siliniyordu --
    // kullanici hangilerinin yeni oldugunu hic goremiyordu. Sunucu kaydi
    // ve simgedeki sayi yine sifirlaniyor; listede ise pencere acik
    // kaldigi surece yeni olanlar vurgulu kaliyor. Bir sonraki acilista
    // okunmus gorunurler.
    try {
      const d = await api.bildirimOkundu();
      setOkunmamis(d?.okunmamis ?? 0);
    } catch (_) { /* yoksay */ }
  };

  const belgeleriGetir = useCallback(async (dosyaId) => {
    setBelgeYukleniyor(true);
    try {
      setBelgeler(await api.belgeler(dosyaId));
    } catch (err) {
      if (err?.response?.status === 401) { cikis(); return; }
      setBelgeler([]);
    } finally {
      setBelgeYukleniyor(false);
    }
  }, [cikis]);

  /*
    Bildirimden gelen belgeyi acar.
    Belge listesi o an yuklu olmayabilir (baska dosyadayken bildirime
    dokunulduysa), bu yuzden dosyanin belgeleri burada yeniden cekilip
    aranir. Belge bulunamazsa sessizce vazgeciliyor: dosya zaten
    aciliyor, muvekkil evragi listeden gorebiliyor.
  */
  const bildirimBelgesiniAc = useCallback(async (belgeId, dosya) => {
    try {
      const dosyaId = dosya?.id ?? dosya?.Id;
      if (!dosyaId) return;
      const liste = await api.belgeler(dosyaId);
      setBelgeler(liste || []);
      const belge = (liste || []).find((b) => String(b.id ?? b.Id) === String(belgeId));
      // Ref uzerinden: onizlemeAc her cizimde yeniden olusuyor, burada
      // eski bir kopyasi kalmasin.
      if (belge) onizlemeAcRef.current?.(belge);
    } catch (e) { /* yoksay */ }
  }, [api]);

  /*
    BILDIRIMDEN GELINDIGINDE: /muvekkil?dosya=12&belge=345
    ------------------------------------------------------
    Telefondaki bildirime dokununca buraya geliniyor. Onceden bu bilgi
    yalnizca portal ILK ACILDIGINDA okunuyordu; portal zaten aciksa
    (uygulama arka plandayken bildirime dokunulunca) hicbir sey
    olmuyordu. Artik adres her degistiginde bakiliyor:
      - dosya seciliyor,
      - belge onizlemede aciliyor,
      - o belgeye ait bildirim okundu isaretleniyor; sunucu da uygulama
        simgesindeki sayiyi guncelliyor.
    Islendikten sonra adres temizleniyor ki geri tusu ayni belgeyi
    tekrar acmasin.
  */
  const location = useLocation();
  const islenenArama = useRef('');
  useEffect(() => {
    if (onizlemeModu || !dosyalar.length) return;
    const arama = location.search || '';
    if (!arama || arama === islenenArama.current) return;

    const p = new URLSearchParams(arama);
    const dosyaId = p.get('dosya');
    const belgeId = p.get('belge');
    if (!dosyaId && !belgeId) return;
    islenenArama.current = arama;

    const dosya = dosyalar.find((d) => String(d.id ?? d.Id) === String(dosyaId));
    if (dosya) setSecili(dosya);

    if (belgeId) {
      if (dosya) bildirimBelgesiniAc(belgeId, dosya);
      api.bildirimOkundu(null, belgeId)
        .then((d) => { setOkunmamis(d?.okunmamis ?? 0); bildirimleriGetir(); })
        .catch(() => { /* yoksay */ });
    }

    navigate('/muvekkil', { replace: true });
  }, [location.search, dosyalar, onizlemeModu, api, navigate, bildirimBelgesiniAc, bildirimleriGetir]);

  const odemeleriGetir = useCallback(async (dosyaId) => {
    setOdemeYukleniyor(true);
    try {
      setOdemeBilgisi(await api.odemeler(dosyaId));
    } catch (_) {
      // Ödeme bilgisi alınamazsa kart gizlenir; dosyanın geri kalanı etkilenmez.
      setOdemeBilgisi(null);
    } finally {
      setOdemeYukleniyor(false);
    }
  }, []);

  /* Telefon bildirimi: dosyaya evrak eklendiginde haber gelsin.
     Onizlemede calismaz — personel muvekkil gozunden bakarken kendi
     telefonunu muvekkilin adina kaydetmemeli. */
  useEffect(() => {
    if (onizlemeModu) return;
    muvekkilBildirimBaslat(navigate);
  }, [onizlemeModu, navigate]);

  useEffect(() => {
    if (secili) belgeleriGetir(secili.id);
  }, [secili, belgeleriGetir]);

  useEffect(() => {
    if (secili) odemeleriGetir(secili.id);
  }, [secili, odemeleriGetir]);

  /**
   * Uzantıdan önizleme türünü çıkarır.
   * 'metin' türü .docx / .udf / .txt içindir; bu dosyalardan metin
   * tarayıcıda çıkarılır (belgeMetniCikar), ek kütüphane gerekmez.
   * Eski ikili .doc (Word 97-2003) ZIP olmadığı için desteklenmez.
   */
  const onizlemeTuru = (belge) => {
    const ad = (belge?.fileName || '').toLowerCase();
    const tip = (belge?.contentType || '').toLowerCase();
    const uzanti = ad.includes('.') ? ad.split('.').pop() : '';
    if (tip.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic'].includes(uzanti)) return 'resim';
    if (tip === 'application/pdf' || uzanti === 'pdf') return 'pdf';
    // Video: iOS yalnizca H.264/mp4 ve mov oynatir; digerleri acilmayabilir.
    if (tip.startsWith('video/') || ['mp4', 'mov', 'm4v', 'webm'].includes(uzanti)) return 'video';
    if (['docx', 'udf', 'txt'].includes(uzanti)) return 'metin';
    return null;
  };

  const onizlemeAc = async (belge) => {
    const tur = onizlemeTuru(belge);
    if (!tur) return;

    // Cok buyuk belgede denemeye hic girmiyoruz; beklemek bosuna.
    if (Number(belge?.fileSize) > ONIZLEME_BOYUT_SINIRI) {
      setOnizleme({ belge, url: null, tur, cokBuyuk: true });
      setOnizlemeYukleniyor(false);
      return;
    }

    // PDF ve video: dosyayi indirmeyi BEKLEMEDEN acilir. Akis adresi
    // parcali (Range) sunuldugu icin video hemen oynamaya baslar, buyuk
    // PDF'in ilk sayfasi tamami inmeden gorunur.
    // Akis ucu yalnizca muvekkil tarafinda var; personel onizlemesinde
    // yoksa asagidaki indirme yoluna dusuyoruz.
    // Akis adresi icin sunucudan kisa omurlu jeton aliniyor; alinamazsa
    // (personel onizlemesinde akis ucu yok) asagidaki indirme yoluna duseriz.
    let akisAdresi = null;
    if (typeof api.akisUrl === 'function') {
      try { akisAdresi = await api.akisUrl(belge.id); }
      catch (_) { akisAdresi = null; }
    }
    if (akisAdresi && (tur === 'pdf' || tur === 'video')) {
      setOnizleme({ belge, url: akisAdresi, tur });
      setOnizlemeYukleniyor(false);
      return;
    }

    setOnizlemeYukleniyor(true);
    // Basta YUZDE DEGIL belirsiz cubuk gosteriliyor: sunucu sikistirilmis
    // yanit dondurdugunde toplam boyut gelmiyor ve yuzde %0'da takili
    // kaliyordu. Boyut gelirse yuzdeye geciliyor.
    // Belgenin boyutu biliniyorsa yuzdeyle basla; bilinmiyorsa belirsiz cubuk.
    setOnizlemeYuzde(Number(belge?.fileSize) > 0 ? 0 : null);
    setOnizlemeInen(0);
    setOnizleme({ belge, url: null, tur });
    try {
      const beklenenBoyut = Number(belge?.fileSize) || 0;
      const { blob, contentType } = await api.belgeIndir(belge.id, true, (olay) => {
        if (!olay?.loaded) return;
        setOnizlemeInen(olay.loaded);
        // Once sunucunun bildirdigi toplam, yoksa belgenin kayitli boyutu.
        const toplam = olay.total || beklenenBoyut;
        if (toplam > 0) {
          setOnizlemeYuzde(Math.min(100, Math.round((olay.loaded / toplam) * 100)));
        }
      });
      const veri = new Blob([blob], { type: contentType || 'application/octet-stream' });

      if (tur === 'metin') {
        const cikti = await belgeMetniCikar(veri, belge.fileName || '');
        setOnizleme({ belge, url: null, tur, metin: cikti?.metin || null, metinHata: cikti?.hata || null });
      } else {
        setOnizleme({ belge, url: URL.createObjectURL(veri), tur });
      }
    } catch (_) {
      // Buyuk belgelerde acilis basarisiz olabiliyor; kullaniciyi ciplak
      // bir hata ile birakmak yerine alternatif yol gosteriliyor.
      setOnizleme({ belge, url: null, tur, acilamadi: true });
    } finally {
      setOnizlemeYukleniyor(false);
      setOnizlemeYuzde(null);
    }
  };
  // Bildirimden gelen belgeyi acan kod en guncel onizlemeAc'i kullansin.
  onizlemeAcRef.current = onizlemeAc;

  const onizlemeKapat = useCallback(() => {
    setOnizleme((o) => {
      // Yalnizca blob adresleri serbest birakilir; akis adresi normal bir URL.
      if (o?.url && o.url.startsWith('blob:')) {
        try { URL.revokeObjectURL(o.url); } catch (_) { /* yoksay */ }
      }
      return null;
    });
  }, []);

  // Esc ile kapansın
  useEffect(() => {
    if (!onizleme) return undefined;
    const tus = (e) => { if (e.key === 'Escape') onizlemeKapat(); };
    window.addEventListener('keydown', tus);
    return () => window.removeEventListener('keydown', tus);
  }, [onizleme, onizlemeKapat]);

  /*
    BELGEYI INDIR
    -------------
    Burada eskiden ayri bir indirme kodu vardi ve uygulamada "Indir"
    dugmesi, onizlenebilen bir belgede yalnizca ONIZLEMEYI YENIDEN
    ACIYORDU -- onizleme zaten acik oldugu icin dugmeye basildiginda
    hicbir sey olmuyor gibi gorunuyordu. Artik personel tarafiyla ayni
    yol kullaniliyor (utils/fileDownload): uygulamada dosya once
    telefona yaziliyor, sonra iOS'un paylasim penceresi aciliyor ve
    "Dosyalara Kaydet" secenegi cikiyor.
  */
  const indir = async (belge) => {
    setDurum('Belge hazırlanıyor…');
    try {
      const { blob, contentType } = await api.belgeIndir(belge.id);
      const sonuc = await downloadBlob(blob, belge.fileName || 'belge', contentType);
      setDurum(sonuc?.method === 'share-cancelled' ? '' : 'Belge kaydedilmeye hazır.');
    } catch (e) {
      setDurum('');
      setHata('Belge indirilemedi. Lütfen tekrar deneyin.');
    }
  };

  /* Fotograf cekme uygulamada AYRI bir yol izliyor: tarayicinin dosya
     secicisi yerine yerli kamera aciliyor. Eskiden secici acilirken iOS
     WebView'i bosaltabiliyordu ve donuste uygulama bastan yukleniyordu
     — kullanici bunu "uygulama kapandi" olarak goruyordu. */
  const fotografCek = async () => {
    if (!secili) return;
    const foto = await evrakFotografiCek();
    if (foto) await dosyayiGonder(foto);
  };

  const yukle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await dosyayiGonder(file);
  };

  const dosyayiGonder = async (file) => {
    if (!file || !secili) return;
    setGonderiliyor(true);
    setYuklemeYuzde(0);
    setDurum('');
    try {
      await api.belgeYukle(secili.id, file, (olay) => {
        if (olay?.total) setYuklemeYuzde(Math.round((olay.loaded / olay.total) * 100));
      });
      setDurum('Belgeniz yüklendi. Büromuz inceleyecektir.');
      await belgeleriGetir(secili.id);
    } catch (err) {
      setDurum(err?.response?.data?.message || 'Belge yüklenemedi.');
    } finally {
      setGonderiliyor(false);
      setYuklemeYuzde(null);
    }
  };

  const [onayVerildi, setOnayVerildi] = useState(() => {
    // Onizlemede (personel "muvekkil gozunden" bakarken) bilgilendirme
    // ekrani HER acilista gorunur: avukat muvekkilin ilk girisinde ne
    // gordugunu her seferinde bastan gorebilsin.
    if (onizlemeModu) return false;
    try { return sessionStorage.getItem(ONAY_ANAHTARI) === '1'; } catch (_) { return false; }
  });

  const onayla = () => {
    // Onizlemede kaydetmiyoruz: personelin tiklamasi, ayni tarayicidan
    // giren bir muvekkilin bilgilendirmeyi atlamasina yol acmasin.
    if (!onizlemeModu) {
      try { sessionStorage.setItem(ONAY_ANAHTARI, '1'); } catch (_) { /* yoksay */ }
    }
    setOnayVerildi(true);
  };

  const buroBelgeleri = useMemo(() => belgeler.filter((b) => !b.muvekkilYukledi), [belgeler]);
  const benimBelgelerim = useMemo(() => belgeler.filter((b) => b.muvekkilYukledi), [belgeler]);

  if (yukleniyor) {
    return <div className="mp-sayfa"><div className="mp-bilgi">Yükleniyor…</div></div>;
  }

  const kalan = secili ? kalanGun(secili.durusmaTarihi) : null;

  return (
    <div className="mp-sayfa">
      {!onayVerildi && (
        <div className="mp-onay-katman" role="dialog" aria-modal="true" aria-labelledby="mp-onay-baslik">
          <div className="mp-onay-kutu">
            <div className="mp-onay-ust">
              <img src="/assets/logo.png" alt="" className="mp-onay-logo" />
              <div>
                <h2 id="mp-onay-baslik">Bilgilendirme</h2>
                <p className="mp-onay-alt">Öztürk Hukuk ve Danışmanlık — Müvekkil Bilgi Sistemi</p>
              </div>
            </div>

            <div className="mp-onay-govde">
              <p className="mp-onay-hitap">
                Sayın <strong>{bilgi.adSoyad || 'Müvekkilimiz'}</strong>,
              </p>

              <p>
                Bu sayfada dosyanıza ilişkin <strong>genel bilgiler</strong> yer almaktadır.
                Buradaki bilgiler dosyanızın anlık durumuyla birebir aynı olmayabilir; adli
                süreçteki gelişmeler sisteme işlenene kadar zaman farkı oluşabilir.
              </p>

              <p>
                Bu nedenle burada gördüğünüz bilgiler <strong>bilgilendirme amaçlıdır</strong> ve
                hukuki görüş ya da beyan niteliği taşımaz. Güncel ve bağlayıcı bilgi için lütfen
                dosyanızdan sorumlu avukatınızla iletişime geçiniz.
              </p>

              <div className="mp-onay-kvkk">
                <h3>Kişisel Verilerin Korunması</h3>
                <p>
                  Bu sistemde yer alan kimlik, iletişim ve dosya bilgileriniz; 6698 sayılı Kişisel
                  Verilerin Korunması Kanunu kapsamında, yalnızca avukatlık sözleşmesinden doğan
                  yükümlülüklerimizin yerine getirilmesi ve hukuki süreçlerinizin yürütülmesi
                  amacıyla işlenmektedir.
                </p>
                <p>
                  Verileriniz üçüncü kişilerle paylaşılmaz; yalnızca kanunen yetkili kurum ve
                  kuruluşlara, mevzuatın zorunlu kıldığı hâllerde aktarılır. Avukatlık meslek
                  sırrı kapsamındaki bilgileriniz gizli tutulur.
                </p>
                <p>
                  Hesabınıza yalnızca size verilen bilgilerle giriş yapılabilir. Giriş
                  bilgilerinizi üçüncü kişilerle paylaşmamanızı, ortak kullanılan cihazlarda
                  işiniz bittiğinde <strong>Çıkış</strong> yapmanızı rica ederiz.
                </p>
              </div>
            </div>

            <div className="mp-onay-alt-satir">
              <button type="button" className="mp-onay-buton" onClick={onayla}>
                Okudum, anladım
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="mp-ust">
        <div className="mp-ust-sol">
          <img src="/assets/logo.png" alt="" className="mp-logo" />
          <div>
            <h1>Öztürk Hukuk ve Danışmanlık</h1>
            <p>Müvekkil Bilgi Sistemi</p>
          </div>
        </div>
        <div className="mp-ust-sag">
          <span className="mp-kisi">{bilgi.adSoyad || 'Müvekkil'}</span>
          <button
            type="button"
            className="mp-zil"
            onClick={bildirimleriAc}
            aria-label={okunmamis > 0 ? `Bildirimler, ${okunmamis} okunmamış` : 'Bildirimler'}
          >
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
                 strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
              <path d="M13.7 21a2 2 0 0 1-3.4 0" />
            </svg>
            {okunmamis > 0 && <span className="mp-zil-sayi">{okunmamis > 99 ? '99+' : okunmamis}</span>}
          </button>
          {!onizlemeModu && (
            <button type="button" className="mp-cikis" onClick={cikis}>Çıkış</button>
          )}
        </div>
      </header>

      {hata && <div className="mp-uyari">{hata}</div>}

      {/* BILDIRIMLER PENCERESI
          Acildigi anda hepsi okundu isaretleniyor. Bir bildirime
          dokununca ilgili dosya seciliyor ve pencere kapaniyor. */}
      {bildirimAcik && (
        <div
          className="mp-bildirim-katman"
          role="dialog"
          aria-modal="true"
          aria-label="Bildirimler"
          onClick={() => setBildirimAcik(false)}
        >
          <div className="mp-bildirim-kutu" onClick={(e) => e.stopPropagation()}>
            <div className="mp-bildirim-ust">
              <strong>Bildirimler</strong>
              <button
                type="button"
                className="mp-bildirim-kapat"
                onClick={() => setBildirimAcik(false)}
                aria-label="Kapat"
              >×</button>
            </div>

            {bildirimYukleniyor ? (
              <p className="mp-bildirim-bos">Yükleniyor…</p>
            ) : bildirimler.length === 0 ? (
              <p className="mp-bildirim-bos">
                Henüz bildiriminiz yok. Dosyanıza evrak eklendiğinde burada görünür.
              </p>
            ) : (
              <ul className="mp-bildirim-liste">
                {bildirimler.map((b) => {
                  const dosya = dosyalar.find((d) => d.id === b.dosyaId);
                  return (
                    <li key={b.id} className={b.okundu ? '' : 'yeni'}>
                      <button
                        type="button"
                        className="mp-bildirim-satir"
                        /* Bildirime dokununca BELGE acilir. Onceden
                           yalnizca dosya seciliyordu; muvekkil "evrak
                           eklendi" bildirimine basip evragi listede
                           kendisi aramak zorunda kaliyordu. */
                        onClick={() => {
                          if (dosya) setSecili(dosya);
                          setBildirimAcik(false);
                          if (b.belgeId) bildirimBelgesiniAc(b.belgeId, dosya);
                        }}
                        disabled={!dosya}
                      >
                        <span className="mp-bildirim-simge" aria-hidden="true">
                          {b.tur === 'durusma' ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3.5" y="5" width="17" height="15" rx="2" />
                              <path d="M3.5 9.5h17M8 3v4M16 3v4" />
                              <path d="M8.5 14h3M8.5 17h6" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                              <path d="M14 3v5h5" />
                              <path d="M9 13h6M9 17h4" />
                            </svg>
                          )}
                        </span>
                        <span className="mp-bildirim-metin">
                          <strong>{b.baslik}</strong>
                          <span>{b.metin}</span>
                          <em>{tarih(b.tarih) || ''}</em>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {onizleme && (
        <div className="mp-onizleme-katman" role="dialog" aria-modal="true" onClick={onizlemeKapat}>
          <div className="mp-onizleme-kutu" onClick={(e) => e.stopPropagation()}>
            <div className="mp-onizleme-ust">
              <strong title={onizleme.belge.fileName}>{onizleme.belge.fileName}</strong>
              <div className="mp-onizleme-islem">
                {/* Artik uygulamada da calisiyor: Filesystem eklentisi dosyayi
                    telefona yaziyor, ardindan iOS paylasim penceresi aciliyor
                    ve "Dosyalara Kaydet" secenegi cikiyor. */}
                {indirmeMumkunMu() && (<button type="button" onClick={() => indir(onizleme.belge)}>İndir</button>)}
                <button type="button" className="mp-onizleme-kapat" onClick={onizlemeKapat} aria-label="Kapat">
                  ×
                </button>
              </div>
            </div>

            <div className="mp-onizleme-govde">
              {onizleme.cokBuyuk ? (
                <div className="mp-onizleme-durum mp-onizleme-hata">
                  <strong>Belge cihazda görüntülenemiyor</strong>
                  <p>
                    Bu belgenin boyutu <strong>{boyut(onizleme.belge.fileSize)}</strong> —
                    telefon veya tablette açılamayacak kadar büyük. Belgeyi web
                    sitemizden giriş yaparak bilgisayarınıza indirebilirsiniz.
                  </p>
                  <a
                    className="mp-onizleme-baglanti"
                    href="https://izzetbaranozturk.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Web sitemize git · izzetbaranozturk.com
                  </a>
                </div>
              ) : onizleme.acilamadi ? (
                <div className="mp-onizleme-durum mp-onizleme-hata">
                  <strong>Belge açılamadı</strong>
                  <p>
                    Belgenin boyutu yüksek olduğundan uygulama içinde
                    görüntülenemedi. Web sitemizden giriş yaparak belgeyi
                    cihazınıza indirebilirsiniz.
                  </p>
                  <a
                    className="mp-onizleme-baglanti"
                    href="https://izzetbaranozturk.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Web sitemize git · izzetbaranozturk.com
                  </a>
                </div>
              ) : onizlemeYukleniyor ? (
                <div className="mp-onizleme-durum">
                  <p>Belge açılıyor…</p>
                  <div className="mp-ilerleme">
                    <div
                      className={`mp-ilerleme-dolu ${onizlemeYuzde === null ? 'belirsiz' : ''}`}
                      style={onizlemeYuzde === null ? undefined : { width: `${onizlemeYuzde}%` }}
                    />
                  </div>
                  <span className="mp-ilerleme-yazi">
                    {onizlemeYuzde === null
                      ? (onizlemeInen > 0 ? `${boyut(onizlemeInen)} indirildi` : 'İndiriliyor…')
                      : `%${onizlemeYuzde} · ${boyut(onizlemeInen)}`}
                  </span>
                  <p className="mp-onizleme-ipucu">
                    Belge büyükse açılması biraz sürebilir. Uzun sürerse web
                    sitemizden giriş yaparak cihazınıza indirebilirsiniz.
                  </p>
                  <a
                    className="mp-onizleme-baglanti"
                    href="https://izzetbaranozturk.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Web sitemize git · izzetbaranozturk.com
                  </a>
                </div>
              ) : onizleme.tur === 'metin' ? (
                onizleme.metin ? (
                  <pre className="mp-onizleme-metin">{onizleme.metin}</pre>
                ) : (
                  <p className="mp-onizleme-durum">
                    {onizleme.metinHata || 'Bu belgenin metni görüntülenemedi.'}
                  </p>
                )
              ) : !onizleme.url ? (
                <p className="mp-onizleme-durum">Belge açılıyor…</p>
              ) : onizleme.tur === 'resim' ? (
                <img src={onizleme.url} alt={onizleme.belge.fileName} className="mp-onizleme-resim" />
              ) : onizleme.tur === 'video' ? (
                <video
                  className="mp-onizleme-video"
                  src={onizleme.url}
                  controls
                  playsInline
                  preload="metadata"
                >
                  Tarayıcınız bu videoyu oynatamıyor.
                </video>
              ) : (
                <PdfGoruntuleyici kaynak={onizleme.url} dosyaAdi={onizleme.belge.fileName} />
              )}
            </div>
          </div>
        </div>
      )}

      {dosyalar.length === 0 ? (
        <div className="mp-bilgi">Görüntülenecek dosyanız bulunmuyor.</div>
      ) : (
        <div className="mp-govde">
          {/* ---- Dosya listesi ---- */}
          <aside className="mp-liste">
            <h3>Dosyalarım <span>{dosyalar.length}</span></h3>
            {dosyalar.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`mp-dosya ${secili?.id === d.id ? 'secili' : ''}`}
                onClick={() => setSecili(d)}
              >
                {/* Föy no büronun iç numarasıdır; müvekkile gösterilmez.
                    Onun yerine dosyayı tanıdığı bilgiler var: dava türü ve
                    varsa mahkeme esas / icra / tahkim numarası. */}
                <span className="mp-dosya-ust">
                  <span className="mp-foy">{turEtiketi(d.davaTuru, d.davaTuruDiger)}</span>
                  <span className={`mp-rozet mp-durum-${d.durum}`}>{durumEtiketi(d.durum)}</span>
                </span>
                {(d.mahkemeEsas || d.icraDosyaNo || d.tahkimBasvuruNo) && (
                  <span className="mp-tur">
                    {d.mahkemeEsas || d.icraDosyaNo || d.tahkimBasvuruNo}
                  </span>
                )}
                {tarih(d.durusmaTarihi) && (
                  <span className="mp-durusma">Duruşma · {tarih(d.durusmaTarihi)}</span>
                )}
              </button>
            ))}
          </aside>

          {/* ---- Dosya detayı ---- */}
          <main className="mp-detay">
            {secili && (
              <>
                <div className="mp-baslik">
                  <div className="mp-baslik-sol">
                    <h2>{turEtiketi(secili.davaTuru, secili.davaTuruDiger)}</h2>
                    <div className="mp-rozetler">
                      {(secili.mahkemeEsas || secili.icraDosyaNo || secili.tahkimBasvuruNo) && (
                        <span className="mp-rozet mp-rozet-tur">
                          {secili.mahkemeEsas || secili.icraDosyaNo || secili.tahkimBasvuruNo}
                        </span>
                      )}
                      <span className={`mp-rozet mp-durum-${secili.durum}`}>{durumEtiketi(secili.durum)}</span>
                    </div>
                  </div>
                  <div className="mp-baslik-sag">
                    {/* Soru sormanın en kolay yolu. Mesaj hangi dosya
                        hakkında olduğu yazılmış halde açılır. */}
                    {buroWhatsapp && (
                      <a
                        className="mp-wa"
                        href={'https://wa.me/' + buroWhatsapp + '?text='
                          + encodeURIComponent(
                              'Merhaba, ' + turEtiketi(secili.davaTuru, secili.davaTuruDiger)
                              + ' dosyam hakkında bilgi almak istiyorum.'
                              + (secili.mahkemeEsas ? ' (Esas: ' + secili.mahkemeEsas + ')' : '')
                            )}
                        target="_blank"
                        rel="noopener noreferrer"
                        // Uygulamada Safari'ye ugramadan dogrudan WhatsApp acilir.
                        // href duruyor ki tarayicida ve saga tiklamada calismaya devam etsin.
                        onClick={(e) => {
                          if (!isNativeApp()) return;
                          e.preventDefault();
                          whatsappAc(
                            buroWhatsapp,
                            'Merhaba, ' + turEtiketi(secili.davaTuru, secili.davaTuruDiger)
                            + ' dosyam hakkında bilgi almak istiyorum.'
                            + (secili.mahkemeEsas ? ' (Esas: ' + secili.mahkemeEsas + ')' : '')
                          );
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
                          <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35Z" />
                          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.17 8.17 0 0 1-1.25-4.36c0-4.53 3.7-8.22 8.23-8.22 2.2 0 4.26.86 5.81 2.41a8.16 8.16 0 0 1 2.41 5.82c0 4.53-3.7 8.21-8.23 8.21Z" />
                        </svg>
                        <span>WhatsApp'tan sor</span>
                      </a>
                    )}

                    {secili.sorumlu && (
                      <div className="mp-sorumlu">
                        <span>Sorumlu Avukat</span>
                        <strong>{secili.sorumlu}</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* ---- Öne çıkan bilgiler ---- */}
                {/* Sistemdeki dosya detayıyla aynı hiyerarşi:
                    duruşma tam genişlikte, mahkeme + esas yan yana (altın vurgulu),
                    dava türü ile durum altta küçük alanlar. */}
                <div className="mp-one-cikan">
                  <div className={`mp-vurgu mp-vurgu-tam ${kalan !== null && kalan <= 15 ? 'mp-vurgu-yakin' : ''}`}>
                    <span className="mp-vurgu-etiket">Duruşma Tarihi ve Saati</span>
                    {formatDurusmaTarihi(secili.durusmaTarihi) ? (
                      <>
                        <strong className="mp-vurgu-deger">{formatDurusmaTarihi(secili.durusmaTarihi)}</strong>
                        {kalan !== null && (
                          <span className="mp-vurgu-alt">
                            {kalan === 0 ? 'Bugün' : `${kalan} gün kaldı`}
                          </span>
                        )}
                      </>
                    ) : (
                      <strong className="mp-vurgu-deger mp-vurgu-bos">
                        Yaklaşan duruşmanız bulunmamaktadır
                      </strong>
                    )}
                  </div>

                  <div className="mp-mahkeme-satir">
                    <div className="mp-vurgu">
                      <span className="mp-vurgu-etiket">Mahkeme Bilgisi</span>
                      <strong className="mp-vurgu-deger">{secili.mahkemeBilgisi || '—'}</strong>
                    </div>
                    <div className="mp-vurgu">
                      <span className="mp-vurgu-etiket">Esas Numarası</span>
                      <strong className="mp-vurgu-deger">
                        {secili.mahkemeEsas || secili.tahkimBasvuruNo || secili.icraDosyaNo || '—'}
                      </strong>
                      {!secili.mahkemeEsas && secili.tahkimBasvuruNo && (
                        <span className="mp-vurgu-alt">Tahkim başvuru numarası</span>
                      )}
                      {!secili.mahkemeEsas && !secili.tahkimBasvuruNo && secili.icraDosyaNo && (
                        <span className="mp-vurgu-alt">İcra dosya numarası</span>
                      )}
                    </div>
                  </div>

                  <div className="mp-kucuk-satir">
                    <div className="mp-kucuk">
                      <span className="mp-kucuk-etiket">Dava Türü</span>
                      <span className="mp-kucuk-deger">
                        {turEtiketi(secili.davaTuru, secili.davaTuruDiger)}
                      </span>
                    </div>
                    <div className="mp-kucuk">
                      <span className="mp-kucuk-etiket">Dosya Durumu</span>
                      <span className="mp-kucuk-deger">{durumEtiketi(secili.durum)}</span>
                    </div>
                  </div>
                </div>

                <Alanlar
                  baslik="Müvekkil Bilgileri"
                  satirlar={[
                    ['Ad Soyad', secili.muvekkil],
                    ['TC Kimlik No', secili.muvekkilTC],
                  ]}
                />

                <Alanlar
                  baslik="Mahkeme ve Esas Bilgileri"
                  satirlar={[
                    ['Mahkeme', secili.mahkemeBilgisi],
                    ['Esas No', secili.mahkemeEsas],
                    ['Tahkim Başvuru No', secili.tahkimBasvuruNo],
                    ['İtiraz Başvuru No', secili.itirazBasvuruNo],
                    ['İcra Dosya No', secili.icraDosyaNo],
                  ]}
                />

                <Alanlar
                  baslik="Tarihler"
                  satirlar={[
                    ['Dosya Geliş Tarihi', tarih(secili.dosyaGelisTarihi)],
                    ['Dava Açılış Tarihi', tarih(secili.davaAcilisTarihi)],
                    ['Tahkim Başvuru Tarihi', tarih(secili.tahkimBasvuruTarihi)],
                    ['Sigorta Başvuru Tarihi', tarih(secili.sigortaBasvuruTarihi)],
                    ['Rapor Tarihi', tarih(secili.raporTarihi)],
                    ['Kesinleşme Tarihi', tarih(secili.kesinlesmeTarihi)],
                  ]}
                />

                <Alanlar
                  baslik="Sigorta Bilgileri"
                  satirlar={[
                    ['Müvekkil Araç Sigorta Şirketi', secili.muvekkilAracSigortaSirketi],
                    ['Karşı Taraf Sigorta Şirketi', secili.karsiTarafSigortaSirketi],
                  ]}
                />

                <Alanlar
                  baslik="Kaza Bilgileri"
                  satirlar={[
                    ['Kaza Tarihi', tarih(secili.kazaTarihi)],
                    ['Kaza Şehri', secili.kazaSehri],
                    ['Müvekkil Plaka', secili.muvekkilPlaka],
                    ['Karşı Taraf Plaka', secili.karsiTarafPlaka],
                    ['Müvekkil Araçtaki Rolü', secili.muvekkilAracRolu],
                  ]}
                />

                {/* ---- Size yapılan ödemeler ---- */}
                <section className="mp-blok mp-odeme-blok">
                  <h4>Size Yapılan Ödemeler</h4>

                  {odemeYukleniyor ? (
                    <p className="mp-bos">Yükleniyor…</p>
                  ) : !odemeBilgisi || odemeBilgisi.adet === 0 ? (
                    <p className="mp-bos">Bu dosyada size yapılmış bir ödeme kaydı bulunmuyor.</p>
                  ) : (
                    <>
                      <div className="mp-odeme-toplam">
                        <span className="mp-odeme-toplam-etiket">Toplam Ödenen</span>
                        <strong className="mp-odeme-toplam-deger">{para(odemeBilgisi.toplam)}</strong>
                        <span className="mp-odeme-toplam-adet">{odemeBilgisi.adet} ödeme</span>
                      </div>

                      <ul className="mp-odemeler">
                        {odemeBilgisi.odemeler.map((o, i) => (
                          <li key={i}>
                            <div className="mp-odeme-sol">
                              <strong>{o.asama}</strong>
                              <span>{tarih(o.tarih) || 'Tarih belirtilmemiş'}</span>
                            </div>
                            <span className="mp-odeme-tutar">{para(o.tutar)}</span>
                          </li>
                        ))}
                      </ul>

                      <p className="mp-odeme-not">
                        Bu liste yalnızca büromuz tarafından size aktarılan tutarları gösterir.
                        Masraflar, vekalet ücreti ve diğer hesap kalemleri burada yer almaz.
                        Güncel hesap dökümü için avukatınızla iletişime geçiniz.
                      </p>
                    </>
                  )}
                </section>

                {/* ---- Belgeler ---- */}
                <p className="mp-belge-uyari">
                  Buradaki bilgiler dosyanızın anlık durumuyla birebir aynı olmayabilir;
                  adli süreçteki gelişmeler sisteme işlenene kadar zaman farkı oluşabilir.
                  Bu nedenle gördüğünüz bilgiler ve belgeler bilgilendirme amaçlıdır ve
                  hukuki görüş ya da beyan niteliği taşımaz. Güncel ve bağlayıcı bilgi için
                  lütfen dosyanızdan sorumlu avukatınızla iletişime geçiniz.
                </p>

                <section className="mp-blok">
                  <h4>Büro Tarafından Paylaşılan Belgeler</h4>
                  {belgeYukleniyor ? (
                    <p className="mp-bos">Yükleniyor…</p>
                  ) : buroBelgeleri.length === 0 ? (
                    <p className="mp-bos">Henüz sizinle paylaşılmış belge bulunmuyor.</p>
                  ) : (
                    <ul className="mp-belgeler">
                      {buroBelgeleri.map((b) => (
                        <li key={b.id}>
                          <div
                            className={`mp-belge-bilgi ${onizlemeTuru(b) ? 'mp-belge-tiklanir' : ''}`}
                            onClick={() => onizlemeTuru(b) && onizlemeAc(b)}
                            role={onizlemeTuru(b) ? 'button' : undefined}
                            tabIndex={onizlemeTuru(b) ? 0 : undefined}
                            onKeyDown={(e) => {
                              if (onizlemeTuru(b) && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                onizlemeAc(b);
                              }
                            }}
                            title={onizlemeTuru(b) ? 'Önizlemek için tıklayın' : undefined}
                          >
                            <strong>{b.fileName}</strong>
                            <span>{boyut(b.fileSize)} · {tarih(b.createdDate)}</span>
                          </div>
                          <div className="mp-belge-islem">
                            {onizlemeTuru(b) && (
                              <button type="button" className="mp-belge-onizle" onClick={() => onizlemeAc(b)}>
                                {isNativeApp() ? 'Görüntüle' : 'Önizle'}
                              </button>
                            )}
                            {/* Uygulamada da calisiyor: belge once telefona yazilip
                                iOS paylasim penceresi aciliyor (bkz. utils/fileDownload). */}
                            {indirmeMumkunMu() && (<button type="button" onClick={() => indir(b)}>İndir</button>)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="mp-blok">
                  <h4>Müvekkil Tarafından Yüklenen Belgeler</h4>
                  {benimBelgelerim.length === 0 ? (
                    <p className="mp-bos">Henüz belge yüklemediniz.</p>
                  ) : (
                    <ul className="mp-belgeler">
                      {benimBelgelerim.map((b) => (
                        <li key={b.id} className="mp-belge-benim">
                          <div
                            className={`mp-belge-bilgi ${onizlemeTuru(b) ? 'mp-belge-tiklanir' : ''}`}
                            onClick={() => onizlemeTuru(b) && onizlemeAc(b)}
                            role={onizlemeTuru(b) ? 'button' : undefined}
                            tabIndex={onizlemeTuru(b) ? 0 : undefined}
                            onKeyDown={(e) => {
                              if (onizlemeTuru(b) && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                onizlemeAc(b);
                              }
                            }}
                            title={onizlemeTuru(b) ? 'Önizlemek için tıklayın' : undefined}
                          >
                            <strong>{b.fileName}</strong>
                            <span>{boyut(b.fileSize)} · Yüklenme: {tarih(b.createdDate)}</span>
                          </div>
                          <div className="mp-belge-islem">
                            {onizlemeTuru(b) && (
                              <button type="button" className="mp-belge-onizle" onClick={() => onizlemeAc(b)}>
                                {isNativeApp() ? 'Görüntüle' : 'Önizle'}
                              </button>
                            )}
                            {/* Uygulamada da calisiyor: belge once telefona yazilip
                                iOS paylasim penceresi aciliyor (bkz. utils/fileDownload). */}
                            {indirmeMumkunMu() && (<button type="button" onClick={() => indir(b)}>İndir</button>)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {gonderiliyor && (
                    <div className="mp-yukleme-durum">
                      <div className="mp-ilerleme">
                        <div
                          className={`mp-ilerleme-dolu ${yuklemeYuzde === null ? 'belirsiz' : ''}`}
                          style={yuklemeYuzde === null ? undefined : { width: `${yuklemeYuzde}%` }}
                        />
                      </div>
                      <span className="mp-ilerleme-yazi">
                        {yuklemeYuzde === null ? 'Yükleniyor…' : `Yükleniyor · %${yuklemeYuzde}`}
                      </span>
                    </div>
                  )}

                  <input ref={dosyaGirdi} type="file" style={{ display: 'none' }} onChange={yukle} />
                  {kameraVarMi() && (
                    <button
                      type="button"
                      className="mp-yukle mp-foto"
                      onClick={fotografCek}
                      disabled={gonderiliyor || onizlemeModu}
                      title={onizlemeModu ? 'Önizlemede belge yüklenemez' : undefined}
                    >
                      Fotoğraf Çek
                    </button>
                  )}
                  <button
                    type="button"
                    className="mp-yukle"
                    onClick={() => dosyaGirdi.current?.click()}
                    // Onizlemede dugme GORUNUR kalir (gorunum birebir ayni olsun diye)
                    // ama calismaz: personel, muvekkil adina belge yukleyemez.
                    disabled={gonderiliyor || onizlemeModu}
                    title={onizlemeModu ? 'Önizlemede belge yüklenemez' : undefined}
                  >
                    {gonderiliyor ? 'Yükleniyor…' : '+ Belge Yükle'}
                  </button>
                  {durum && <p className="mp-durum">{durum}</p>}
                </section>
              </>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default MuvekkilPortal;
